
import { Controller } from "@hotwired/stimulus"
import noUiSlider from "nouislider"
import maplibregl from "maplibre-gl"
import Chart from "chart.js/auto"
import html2canvas from "html2canvas-pro"

export default class extends Controller {
  static targets = [
    "map",
    "animalClassSelect",
    "animalSpeciesSelect",
    "originSelect",
    "citySelect",
    "infoSubtitle",
    "infoTitle",
    "opacityPopup",
    "rangeSlider",
    "rankingSubtitle",
    "rankingTitle",
    "roadCitySelect",
    "roadSelect",
    "container",
    "statsSection",
    "mapWrapper",
    "chevronButton"
  ]

  static values = {
    zoom: { type: Number, default: 6 },
    latitude: { type: Number, default: -20.51 },
    longitude: { type: Number, default: -54.54 },
    layersUrl: { type: String, default: "/pages/layers.json" },
    occurrencesUrl: { type: String, default: "/pages/occurrences.json" }
  }

  connect() {
    this.activeLayerIds = []
    this.layerOpacities = {}
    this.currentPopup = null

    const filterTargets = [
      this.citySelectTarget, this.roadSelectTarget, this.animalClassSelectTarget, this.animalSpeciesSelectTarget, this.originSelectTarget
    ].filter(Boolean)

    filterTargets.forEach(target =>
      target.addEventListener("change", () => this.debouncedLoadOccurrences())
    )

    this.setupLayerToggles()
    this.setupAnimalFiltering()
    this.setupRoadCityToggle()
    this.setupCitySelect()

    const startDate = { year: this.rangeSliderTarget.dataset["startYear"], month: this.rangeSliderTarget.dataset["startMonth"] }
    const endDate = { year: this.rangeSliderTarget.dataset["endYear"], month: this.rangeSliderTarget.dataset["endMonth"] }

    const startValue = this.toSliderValue(startDate)
    const endValue = this.toSliderValue(endDate)

    noUiSlider.create(this.rangeSliderTarget, {
      start: [startValue, endValue],
      connect: true,
      step: 1,
      range: {
        min: this.toSliderValue(this.getMonthsBefore(startDate)),
        max: this.toSliderValue(this.getMonthsAfter(endDate))
      },
      tooltips: [
        {
          to: (v) => {
            const d = this.fromSliderValue(v)
            return `${String(d.month).padStart(2, '0')}/${d.year}`
          }
        },
        {
          to: (v) => {
            const d = this.fromSliderValue(v)
            return `${String(d.month).padStart(2, '0')}/${d.year}`
          }
        }
      ],
      format: {
        to: (v) => v,
        from: (v) => parseFloat(v)
      }
    })

    this.rangeSliderTarget.noUiSlider.on('change', (values) => {
      const minValue = this.toSliderValue(startDate);
      const maxValue = this.toSliderValue(endDate);

      let [val0, val1] = values.map(v => parseFloat(v));
      let changed = false;
      if (val0 < minValue) {
        val0 = minValue;
        changed = true;
      }
      if (val1 > maxValue) {
        val1 = maxValue;
        changed = true;
      }

      if (changed) {
        this.rangeSliderTarget.noUiSlider.set([val0, val1]);
      } else {
        this.loadOccurrences();
      }
    });


    this.debouncedLoadLayers = this.debounce(this.loadLayers.bind(this), 300)
    this.debouncedLoadOccurrences = this.debounce(this.loadOccurrences.bind(this), 300)

    this.initializeMap()
  }

  setupAnimalFiltering() {
    if (!this.hasAnimalClassSelectTarget || !this.hasAnimalSpeciesSelectTarget) return

    const classSelect = this.animalClassSelectTarget
    const speciesSelect = this.animalSpeciesSelectTarget
    const allOptions = Array.from(speciesSelect.options)

    classSelect.addEventListener("change", () => {
      const selectedClass = classSelect.value
      const tom = speciesSelect.tomselect

      if (!tom) return

      tom.clear()
      tom.clearOptions()

      const optionsToAdd = allOptions.filter(opt => {
        return opt.value !== "" && (
          selectedClass === "" || opt.dataset.class === selectedClass
        )
      })

      optionsToAdd.forEach(opt => {
        tom.addOption({
          value: opt.value,
          text: opt.text
        })
      })

      tom.refreshOptions(false)
      tom.clear(true) // garante placeholder visível

      tom.trigger("change")
    })
  }


  setupRoadCityToggle() {
    if (!this.hasRoadCitySelectTarget || !this.hasCitySelectTarget || !this.hasRoadSelectTarget) return

    const roadCityRadios = this.roadCitySelectTarget.querySelectorAll('input[type="radio"]')
    const citySelect = this.citySelectTarget
    const roadSelect = this.roadSelectTarget

    const firstRadio = roadCityRadios[0]
    firstRadio.checked = true
    citySelect.parentElement.classList.remove("hidden")
    roadSelect.parentElement.classList.add("hidden")

    const resetSelect = (select) => {
      select.selectedIndex = 0
    }

    const updateButtonStyles = () => {
      roadCityRadios.forEach(radio => {
        const span = radio.nextElementSibling
        if (radio.checked) {
          span.classList.add("bg-[#A16B44]", "text-white")
          span.classList.remove("bg-[#F9F8F6]", "text-gray-700", "hover:text-white")
        } else {
          span.classList.remove("bg-[#A16B44]", "text-white")
          span.classList.add("bg-[#F9F8F6]", "text-gray-700", "hover:text-white")
        }
      })
    }

    updateButtonStyles()

    roadCityRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        const selected = radio.value

        if (selected === "Rodovias") {
          roadSelect.parentElement.classList.remove("hidden")
          citySelect.parentElement.classList.add("hidden")
          resetSelect(citySelect)
        } else {
          citySelect.parentElement.classList.remove("hidden")
          roadSelect.parentElement.classList.add("hidden")
          resetSelect(roadSelect)
        }

        this.debouncedLoadOccurrences()

        // Dispara o change no select visível
        const event = new Event("change", { bubbles: true })
        const targetSelect = selected === "Rodovias" ? roadSelect : citySelect
        targetSelect.dispatchEvent(event)

        // Atualiza a cor do botão selecionado
        updateButtonStyles()
      })
    })
  }

  setupCitySelect() {
    if (!this.hasCitySelectTarget) return

    this.citySelectTarget.addEventListener("change", async () => {
      const cityValue = this.citySelectTarget.value

      // Carrega ocorrências normalmente
      await this.loadOccurrences()

      // FlyTo apenas quando mudar a cidade
      if (cityValue === "") {
        this.map.flyTo({
          center: [this.longitudeValue, this.latitudeValue],
          zoom: this.zoomValue,
          essential: true
        })
      } else {
        // Se tiver coordenadas da cidade, faça o fly
        const option = this.citySelectTarget.selectedOptions[0]
        const lat = parseFloat(option.dataset.latitude)
        const lng = parseFloat(option.dataset.longitude)
        if (!isNaN(lat) && !isNaN(lng)) {
          this.map.flyTo({
            center: [lng, lat],
            zoom: 9,
            essential: true
          })
        }
      }
    })
  }

  initializeMap() {
    if (!this.hasMapTarget) return console.error("Map target not found!")

    this.map = new maplibregl.Map({
      container: this.mapTarget,
      style: {
        version: 8,
        sources: {
          'satellite-tiles': {
            type: 'raster',
            tiles: [
              "https://mt0.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
              "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
              "https://mt2.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
              "https://mt3.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
            ],
            tileSize: 256,
            attribution: 'Google',
          },
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution:
              '<a href="https://maplibre.org/">MapLibre</a> | &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          },
        },
        layers: [
          { id: "osm", type: "raster", source: "osm", layout: { visibility: "visible" } },
          { id: 'satellite', type: 'raster', source: 'satellite-tiles', layout: { visibility: "none" } }
        ],
      },
      center: [this.longitudeValue, this.latitudeValue],
      zoom: this.zoomValue,
      attributionControl: false
    })

    const baseLayerControl = {
      onAdd: (map) => {
        const container = document.createElement("div")
        container.className = "maplibregl-ctrl custom-map-control"

        container.innerHTML = `
          <div class="flex rounded-xl overflow-hidden shadow-md border border-gray-200 map-buttons">
            <button data-type="osm"
              class="base-btn active px-3 py-2 text-xs font-medium bg-white text-gray-800">
              Mapa
            </button>
            <button data-type="satellite"
              class="base-btn px-3 py-2 text-xs font-medium bg-white text-gray-500">
              Satélite
            </button>
          </div>
        `

        const buttons = container.querySelectorAll(".base-btn")

        buttons.forEach(btn => {
          btn.addEventListener("click", () => {
            const type = btn.dataset.type

            // troca camada
            this.toggleBaseLayer(type)

            // atualiza estilo visual
            buttons.forEach(b => {
              b.classList.remove("active", "bg-[#A16B44]", "text-white")
              b.classList.add("bg-white", "text-gray-500")
            })

            btn.classList.add("active", "bg-[#A16B44]", "text-white")
            btn.classList.remove("text-gray-500")
          })
        })

        return container
      },

      onRemove: () => { }
    }

    this.map.addControl(
      new maplibregl.AttributionControl({
        compact: false,
        customAttribution: "",
        className: "maplibregl-ctrl-bottom-bar",
      }),
      "top-right"
    )

    this.map.addControl(baseLayerControl, "top-right")

    this.map.addControl(new maplibregl.NavigationControl())

    const downloadMapControl = {
      onAdd: (map) => {
        const btn = document.createElement("button")
        btn.className = "bg-white rounded shadow hover:bg-gray-100 w-10 h-10 inline-flex items-center justify-center"
        btn.title = "Download do mapa"
        btn.innerHTML = `<svg class="m-auto" width="20" height="21" viewBox="0 0 24 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.99999 15.2617L12 19.2617M12 19.2617L16 15.2617M12 19.2617V10.2617M20.88 16.3517C21.7494 15.7403 22.4014 14.8678 22.7413 13.8608C23.0812 12.8538 23.0914 11.7647 22.7704 10.7515C22.4494 9.73828 21.8139 8.85374 20.9561 8.22621C20.0983 7.59868 19.0628 7.26082 18 7.26165H16.74C16.4392 6.08952 15.8765 5.0009 15.0941 4.07773C14.3117 3.15456 13.3301 2.42091 12.2232 1.93201C11.1163 1.4431 9.91284 1.21168 8.70352 1.25517C7.4942 1.29866 6.31051 1.61592 5.24155 2.18308C4.17259 2.75023 3.24622 3.5525 2.53218 4.52948C1.81814 5.50646 1.33505 6.6327 1.11925 7.8234C0.903464 9.0141 0.960604 10.2382 1.28637 11.4037C1.61214 12.5691 2.19805 13.6454 2.99999 14.5517" stroke="#A16B44" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>`
        btn.onclick = () => this.downloadMapAsImage()
        const container = document.createElement("div")
        container.className = "maplibregl-ctrl maplibregl-ctrl-group"
        container.appendChild(btn)
        return container
      },
      onRemove: (map) => { },
    }

    this.map.addControl(downloadMapControl, "top-right")

    this.map.on("load", async () => {
      await this.loadLayers()
      await this.loadOccurrences()

      // Atualiza ao mover ou dar zoom no mapa
      this.map.on("moveend", () => {
        this.debouncedLoadLayers()
        this.debouncedLoadOccurrences()
      })
    })
  }

  toggleBaseLayer(type) {
    this.map.setLayoutProperty("osm", "visibility", type === "osm" ? "visible" : "none")
    this.map.setLayoutProperty("satellite", "visibility", type === "satellite" ? "visible" : "none")
  }

  setupLayerToggles() {
    this.layerCheckboxes = document.querySelectorAll('.toggle-switch input[type="checkbox"]')

    this.layerCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (event) => this.toggleLayer(event))
    })
  }

  toggleLayer(event) {
    const checkbox = event.target
    const layerId = checkbox.value
    const color = checkbox.dataset.color || "#f59e0b"
    const publicShow = checkbox.dataset.publicShow === "true"

    const mapLayerId = `${layerId}-points`

    this.clickableLayerIds ||= []
    const exists = this.activeLayerIds.find(l => l.id === layerId)

    if (!exists) {
      if (checkbox.checked) {
        this.activeLayerIds.push({ id: layerId, color: color })

        if (publicShow && !this.clickableLayerIds.includes(mapLayerId)) {
          this.clickableLayerIds.push(mapLayerId)
        }
      }
    } else {
      if (!checkbox.checked) {
        this.activeLayerIds = this.activeLayerIds.filter(l => l.id !== layerId)
        this.clickableLayerIds = this.clickableLayerIds.filter(id => id !== mapLayerId)
      }
    }

    const opacityBtn = document.querySelector(`button[data-layer-id="${layerId}"]`)
    if (opacityBtn) {
      opacityBtn.disabled = !checkbox.checked
      if (checkbox.checked) {
        opacityBtn.classList.remove("opacity-disabled")
        opacityBtn.classList.add("opacity-enabled")
      } else {
        opacityBtn.classList.remove("opacity-enabled")
        opacityBtn.classList.add("opacity-disabled")
      }
    }

    this.debouncedLoadLayers()
  }

  debounce(func, wait) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), wait)
    }
  }

  async loadLayers() {
    if (!this.hasLayersUrlValue) return

    // se não há layers ativas limpa a source de layers
    if (this.activeLayerIds.length === 0) {
      this.clearLayerFeatures()
      this.applyLayerOpacitiesAndColors() // garante paint atualizados
      return
    }

    try {
      const bounds = this.map.getBounds()
      const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()].join(",")

      const params = new URLSearchParams({
        bbox: bbox
      })

      this.activeLayerIds.forEach(l => params.append("layer_ids[]", l.id))
      const url = `${this.layersUrlValue}?${params.toString()}`

      const response = await fetch(url)
      const data = await response.json()
      const features = data.features || []

      // aplica cor às features recebidas (garante color property)
      const colored = features.map(f => {
        if (f.properties && f.properties.featurable_type === "Layer") {
          const layer = this.activeLayerIds.find(l => l.id.toString() === String(f.properties.featurable_id))
          if (layer) f.properties.color = layer.color
        }
        return f
      })

      this.updateGeoJSONSource("layers-source", colored)
      this.applyLayerOpacitiesAndColors()
      this.bindPublicLayerInteractions("layers-source")

    } catch (error) {
      console.error("Erro ao carregar layers:", error)
    }
  }

  async loadOccurrences() {
    if (!this.hasOccurrencesUrlValue) return

    try {
      const bounds = this.map.getBounds()
      const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()].join(",")

      const params = new URLSearchParams({
        bbox: bbox
      })

      // Filtros ativos
      if (this.hasCitySelectTarget && this.citySelectTarget.value)
        params.append("city", this.citySelectTarget.value)
      if (this.hasRoadSelectTarget && this.roadSelectTarget.value)
        params.append("road", this.roadSelectTarget.value)
      if (this.hasAnimalClassSelectTarget && this.animalClassSelectTarget.value)
        params.append("animal_class", this.animalClassSelectTarget.value)
      if (this.hasAnimalSpeciesSelectTarget && this.animalSpeciesSelectTarget.value)
        params.append("animal_species", this.animalSpeciesSelectTarget.value)
      if (this.hasOriginSelectTarget && this.originSelectTarget.value)
        params.append("origin", this.originSelectTarget.value)

      const [startDate, endDate] = this.getDateRange()
      if (startDate && endDate) {
        params.append("start_year", startDate.year)
        params.append("start_month", startDate.month)
        params.append("end_year", endDate.year)
        params.append("end_month", endDate.month)
      }

      const url = `${this.occurrencesUrlValue}?${params.toString()}`
      const response = await fetch(url)
      const data = await response.json()

      this.updateStats(data.stats);

      if (data.features) {
        this.updateGeoJSONSource("occurrences-source", data.features)
        this.bindLayerInteractions("occurrences-source")
      }
    } catch (error) {
      console.error("Erro ao carregar ocorrências:", error)
    }
  }

  updateGeoJSONSource(sourceId, features) {
    const geojson = { type: "FeatureCollection", features }

    if (!this.map.getSource(sourceId)) {
      this.map.addSource(sourceId, { type: "geojson", data: geojson })

      // points
      this.map.addLayer({
        id: `${sourceId}-points`,
        type: "circle",
        source: sourceId,
        filter: ["==", ["geometry-type"], "Point"],
        paint: {
          "circle-radius": 6,
          "circle-color": ["coalesce", ["get", "color"], "#A16B44"],
          "circle-stroke-width": 1,
          "circle-stroke-color": "#fff",
          "circle-opacity": 1
        }
      })

      // lines
      this.map.addLayer({
        id: `${sourceId}-lines`,
        type: "line",
        source: sourceId,
        filter: ["any", ["==", ["geometry-type"], "LineString"], ["==", ["geometry-type"], "MultiLineString"]],
        paint: {
          "line-color": ["coalesce", ["get", "color"], "#A16B44"],
          "line-width": 2,
          "line-opacity": 1
        }
      })

      // polígonos e multi-polígonos
      this.map.addLayer({
        id: `${sourceId}-polygons`,
        type: "line",
        source: sourceId,
        filter: ["any", ["==", ["geometry-type"], "Polygon"], ["==", ["geometry-type"], "MultiPolygon"]],
        paint: {
          "line-color": ["coalesce", ["get", "color"], "#A16B44"],
          "line-width": 2,
          "line-opacity": 1
        }
      })

      // this.map.moveLayer("layers-source-points")
      // this.map.moveLayer("layers-source-lines")
      // this.map.moveLayer("layers-source-polygons")
    } else {
      this.map.getSource(sourceId).setData(geojson)
    }
  }

  clearLayerFeatures() {
    if (this.map.getSource("layers-source"))
      this.map.getSource("layers-source").setData({ type: "FeatureCollection", features: [] })
  }

  applyLayerOpacitiesAndColors() {
    // Atualiza cores das features nos sources
    ['layers-source', 'occurrences-source'].forEach(sourceId => {
      const src = this.map.getSource(sourceId)
      if (!src) return
      try {
        const data = src._data || (src._options && src._options.data)
        const features = (data && data.features) ? data.features : []
        const updated = features.map(f => {
          if (f.properties && f.properties.featurable_type === "Layer") {
            const layer = this.activeLayerIds.find(l => l.id.toString() === String(f.properties.featurable_id))
            if (layer) f.properties.color = layer.color
          }
          return f
        })
        src.setData({ type: "FeatureCollection", features: updated })
      } catch (e) {
        console.warn("Erro ao atualizar cores do source", sourceId, e)
      }
    })

    // Atualiza opacidades das camadas
    const paintTargets = [
      { suffix: "-points", prop: "circle-opacity", default: 1 },
      { suffix: "-lines", prop: "line-opacity", default: 1 },
      { suffix: "-polygons", prop: "line-opacity", default: 1 }
    ]

    paintTargets.forEach(({ suffix, prop, default: defaultOpacity }) => {
      ['layers-source', 'occurrences-source'].forEach(sourceId => {
        const mapLayerId = `${sourceId}${suffix}`
        if (!this.map.getLayer(mapLayerId)) return

        const entries = Object.entries(this.layerOpacities)
        let expr

        if (entries.length) {
          expr = ["case"]
          entries.forEach(([fid, op]) => {
            expr.push(["==", ["get", "featurable_id"], parseInt(fid)], op)
          })
          expr.push(defaultOpacity)
        } else {
          expr = defaultOpacity
        }

        try {
          this.map.setPaintProperty(mapLayerId, prop, expr)
        } catch (err) {
          console.warn("Erro ao setPaintProperty", mapLayerId, prop, err)
        }
      })
    })
  }

  updateStats(stats) {
    if (!stats) return

    const [startDate, endDate] = this.getDateRange()

    const topSpecies = stats.species_ranking.slice(0, 10);
    const labels = topSpecies.map(item => item.name); // Nome da espécie
    const counts = topSpecies.map(item => item.count); // Quantidade

    // Títulos dinâmicos
    const roadCityRadio = this.roadCitySelectTarget.querySelector('input[type="radio"]:checked')
    const citySelect = this.hasCitySelectTarget ? this.citySelectTarget : null
    const roadSelect = this.hasRoadSelectTarget ? this.roadSelectTarget : null
    const animalClass = this.hasAnimalClassSelectTarget ? this.animalClassSelectTarget.value : null
    const animalSpecies = this.hasAnimalSpeciesSelectTarget ? this.animalSpeciesSelectTarget.value : null
    const origin = this.hasOriginSelectTarget ? this.originSelectTarget.value : null

    const cityText = citySelect?.options[citySelect.selectedIndex]?.text
    const roadText = roadSelect?.options[roadSelect.selectedIndex]?.text

    let areaLabel = ""
    if (roadCityRadio.value === "Rodovias" && roadText) {
      areaLabel = roadText === "Rodovia" ? "Todas as Rodovias de MS" : roadText
    } else if (roadCityRadio.value === "Município" && cityText) {
      areaLabel = cityText
    }

    let animalLabel = ""
    if (animalClass && animalSpecies) {
      animalLabel = ` (${animalClass} / ${animalSpecies})`
    } else if (animalClass) {
      animalLabel = ` (${animalClass})`
    }

    let titleSuffix = ""
    if (areaLabel) {
      titleSuffix = ` — ${areaLabel}${animalLabel}`
    } else if (animalLabel) {
      titleSuffix = ` — ${animalLabel}`
    }

    if (origin) {
      titleSuffix = `${titleSuffix} — ${origin}`
    }

    this.rankingTitleTarget.textContent = `Ranking de Espécies${titleSuffix}`;
    this.infoTitleTarget.textContent = `Estatísticas gerais de Ocorrências${titleSuffix}`

    const subtitle = (startDate && endDate) ? `de ${startDate.month}/${startDate.year} a ${endDate.month}/${endDate.year}` : ""
    this.rankingSubtitleTarget.textContent = subtitle
    this.infoSubtitleTarget.textContent = subtitle

    // Atualiza nomes de arquivos para download
    document.querySelector("#ranking-card button").dataset["filename"] = `Ranking de Ocorrências${titleSuffix}${subtitle ? ' - ' + subtitle : ''}.png`
    document.querySelector("#info-card button").dataset["filename"] = `Ranking de Ocorrências${titleSuffix}${subtitle ? ' - ' + subtitle : ''}.png`

    // Atualiza contadores
    document.getElementById("totalOccurrences").textContent = stats.info.total.toLocaleString()
    document.getElementById("averagePerYear").textContent = stats.info.average_per_year.toLocaleString()
    document.getElementById("topYear").textContent = stats.info.top_year
    document.getElementById("topYearCount").textContent = stats.info.top_year_count.toLocaleString()

    document.querySelectorAll(".originsInfo").forEach(el => el.textContent = stats.info.origins_info)

    // Atualiza gráfico
    const ctx = document.getElementById("rankingList").getContext("2d");
    if (this.chart) this.chart.destroy();

    const backgroundColors = counts.map((_, index) =>
      index === 0 ? "#faba01" : "#a06b45"
    );

    this.chart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [{
          label: "Registros",
          data: counts,
          backgroundColor: backgroundColors,
          borderRadius: 5
        }]
      },
      options: {
        // indexAxis: 'y', // Transformar em barras horizontais
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: { beginAtZero: true, grid: { display: false } },
          y: {
            ticks: {
              autoSkip: false,
              font: { size: 10 }
            }
          }
        }
      }
    });
  }

  bindLayerInteractions(sourceId) {
    if (this.currentPopup) {
      this.currentPopup.remove()
    }

    const layers = [`${sourceId}-points`, `${sourceId}-lines`]

    layers.forEach(layerId => {
      if (!this.map.getLayer(layerId)) return

      if (!this.boundLayers) this.boundLayers = new Set()
      if (this.boundLayers.has(layerId)) return
      this.boundLayers.add(layerId)

      // clique em pontos
      this.map.on("click", layerId, async (e) => {
        const feature = e.features[0]
        if (!feature) return

        // se houver ponto de camada, ignora clique para evitar conflito de popups
        const featuresAtPoint = this.map.queryRenderedFeatures(e.point, {
          layers: ["layers-source-points"]
        })

        if (featuresAtPoint.length > 0) {
          return
        }
        const coords = feature.geometry.coordinates.slice()
        const id = feature.properties.id

        try {
          const response = await fetch(`/features/${id}`)
          const data = await response.json()
          const html = `
            <div class="bg-white rounded-xl p-3">
              <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-800">
                <div>
                  <p class="text-gray-500 font-medium mb-1">Equipe</p>
                  <p class="font-semibold">${data.properties["Equipe"] || "—"}</p>
                </div>
                <div>
                  <p class="text-gray-500 font-medium mb-1">Registro</p>
                  <p class="font-semibold">${new Date(data.identified_at).toLocaleDateString("pt-BR") || "—"}</p>
                </div>
                <div>
                  <p class="text-gray-500 font-medium mb-1">Rodovia</p>
                  <p class="font-semibold">${data.road || "—"}</p>
                </div>
                <div>
                  <p class="text-gray-500 font-medium mb-1">Município</p>
                  <p class="font-semibold">${data.city_name || "—"}</p>
                </div>
                <div>
                  <p class="text-gray-500 font-medium mb-1">Espécie</p>
                  <p class="font-semibold">${data.animal_species || "—"}</p>
                </div>
                <div>
                  <p class="text-gray-500 font-medium mb-1">Classe</p>
                  <p class="font-semibold">${data.animal_class || "—"}</p>
                </div>
              </div>
              <div class="grid grid-cols-1 pt-2 text-sm text-gray-800">
                <p class="font-semibold"><em>${data.animal_sci_name || "—"}</em></p>
              </div>
              <div class="grid grid-cols-1 pt-2 text-sm text-gray-800">
                <div class="text-center ">
                  <p class="text-gray-500 font-medium mb-1">
                    Lat: ${data.properties["x"] || "—"},
                    Long: ${data.properties["y"] || "—"}
                  </p>
                </div>
              </div>
            </div>
          `

          this.currentPopup = new maplibregl.Popup({ className: 'custom-popup', closeButton: true, closeOnClick: true })
            .setLngLat(coords)
            .setHTML(html)
            .addTo(this.map)
        } catch (err) {
          console.error("Erro ao carregar dados do ponto:", err)
        }
      })

      // hover cursor
      this.map.on("mouseenter", layerId, () => this.map.getCanvas().style.cursor = "pointer")
      this.map.on("mouseleave", layerId, () => this.map.getCanvas().style.cursor = "")
    })
  }

  toSliderValue({ year, month }) {
    return year * 12 + (month - 1)
  }

  fromSliderValue(value) {
    const year = Math.floor(value / 12)
    const month = (value % 12) + 1
    return { year, month }
  }


  getDateRange() {
    if (!this.rangeSliderTarget || !this.rangeSliderTarget.noUiSlider) return []
    const [startValue, endValue] = this.rangeSliderTarget.noUiSlider.get().map(v => parseFloat(v))
    return [this.fromSliderValue(startValue), this.fromSliderValue(endValue)]
  }

  downloadCardAsImage(event) {
    const button = event.currentTarget
    const cardSelector = button.dataset.cardSelector
    const filename = button.dataset.filename || "download.png"

    const container = document.querySelector(cardSelector)
    if (!container) return

    // Esconde temporariamente o botão de download
    const downloadButtons = container.querySelectorAll('[data-action="click->map#downloadCardAsImage"]')
    downloadButtons.forEach(btn => btn.style.display = "none")

    const canvas = container.querySelector("canvas")
    let replacement = null
    if (canvas) {
      const img = document.createElement("img")
      img.src = canvas.toDataURL("image/png")
      img.style.width = canvas.style.width || canvas.width + "px"
      img.style.height = canvas.style.height || canvas.height + "px"
      canvas.style.display = "none"
      canvas.parentElement.appendChild(img)
      replacement = { canvas, img }
    }

    html2canvas(container, { logging: false, useCORS: true }).then(canvasCaptured => {
      const link = document.createElement("a")
      link.download = filename
      link.href = canvasCaptured.toDataURL("image/png")
      link.click()

      // Restaura o botão
      downloadButtons.forEach(btn => btn.style.display = "block")

      if (replacement) {
        replacement.img.remove()
        replacement.canvas.style.display = "block"
      }
    })
  }


  downloadMapAsImage() {
    if (!this.map) return

    const controls = this.mapTarget.querySelectorAll(".maplibregl-ctrl:not(.maplibregl-ctrl-attrib)")
    controls.forEach(c => c.style.display = "none")

    this.map.once("render", () => {
      try {
        const mapCanvas = this.map.getCanvas()
        const img = new Image()
        img.src = mapCanvas.toDataURL("image/png")

        img.onload = () => {
          // Cria canvas temporário 2D
          const tmpCanvas = document.createElement("canvas")
          tmpCanvas.width = mapCanvas.width
          tmpCanvas.height = mapCanvas.height
          const ctx = tmpCanvas.getContext("2d")
          if (!ctx) throw new Error("Não foi possível criar contexto 2D")

          // Desenha mapa
          ctx.drawImage(img, 0, 0)

          // Define contributor
          const attribution = "MapLibre | © OpenStreetMap contributors"
          ctx.font = "12px sans-serif"
          const padding = 6
          const textMetrics = ctx.measureText(attribution)
          const rectWidth = textMetrics.width + padding * 2
          const rectHeight = 16 + padding // altura aproximada do texto

          // Posição: canto inferior direito
          const x = tmpCanvas.width - rectWidth - 2
          const y = rectHeight - 20

          // Fundo cinza atrás do contributor
          ctx.fillStyle = "#f3f3f3"
          ctx.fillRect(x, y, rectWidth, rectHeight)

          // Texto do contributor
          ctx.fillStyle = "rgba(0,0,0,0.6)"
          ctx.textAlign = "left"
          ctx.textBaseline = "top"
          ctx.fillText(attribution, x + padding, y + padding)

          // Download
          const link = document.createElement("a")
          link.download = "map.png"
          link.href = tmpCanvas.toDataURL("image/png")
          link.click()
        }
      } catch (err) {
        console.error("Erro ao gerar imagem do mapa:", err)
      } finally {
        controls.forEach(c => c.style.display = "block")
      }
    })

    this.map.triggerRepaint()
  }


  toggleLayerInfo(event) {
    event.stopPropagation()

    const button = event.currentTarget
    const info = JSON.parse(button.dataset.layerInfo)

    const existingTooltip = button.parentElement.querySelector(".layer-tooltip")
    if (existingTooltip) {
      existingTooltip.remove()
      return
    }

    document.querySelectorAll(".layer-tooltip").forEach(el => el.remove())

    const tooltip = document.createElement("div")
    tooltip.className = `
      absolute z-50 bottom-full mb-2 right-0 bg-white border border-gray-300 
      rounded-lg shadow-md p-2 w-56 layer-tooltip text-sm text-gray-700
    `
    tooltip.innerHTML = `
      <div class="flex items-center gap-2 mb-1">
        <span class="w-3 h-3 rounded-full" style="background-color: ${info.color};"></span>
        <span class="font-semibold text-gray-800">${info.name}</span>
      </div>
      <p class="text-xs leading-snug"><strong>Fonte:</strong> ${info.source || "—"}</p>
      <p class="text-xs leading-snug"><strong>Atualização:</strong> ${info.update_date || "—"}</p>
      ${info.description ? `<p class="text-xs leading-snug">${info.description}</p>` : ""}
      <div class="absolute -bottom-1 right-2 w-2 h-2 bg-white border-r border-b border-gray-300 rotate-45"></div>
    `

    button.parentElement.appendChild(tooltip)

    const closeTooltip = (e) => {
      if (!tooltip.contains(e.target) && e.target !== button) {
        tooltip.remove()
        document.removeEventListener("click", closeTooltip)
      }
    }

    document.addEventListener("click", closeTooltip)
  }

  showTooltip(event) {
    const text = event.currentTarget.dataset.tooltipText
    if (!text) return

    // Cria tooltip
    this.tooltip = document.createElement("div")
    this.tooltip.textContent = text
    this.tooltip.className =
      "fixed bg-gray-800 text-white text-xs rounded py-1 px-2 shadow-lg z-[9999] pointer-events-none transition-opacity duration-150 opacity-0"

    document.body.appendChild(this.tooltip)

    // Calcula posição
    const rect = event.currentTarget.getBoundingClientRect()
    this.tooltip.style.top = `${rect.top - 8}px`
    this.tooltip.style.left = `${rect.left + rect.width / 2}px`
    this.tooltip.style.transform = "translate(-50%, -100%)"

    // Fade-in suave
    requestAnimationFrame(() => (this.tooltip.style.opacity = "1"))
  }

  hideTooltip() {
    if (this.tooltip) {
      this.tooltip.style.opacity = "0"
      setTimeout(() => {
        this.tooltip?.remove()
        this.tooltip = null
      }, 150)
    }
  }

  toggleOpacityPopup(event) {
    event.stopPropagation()

    const button = event.currentTarget
    const layerId = button.dataset.layerId
    const popup = this.opacityPopupTargets.find(el => el.dataset.layerId === layerId)

    if (!popup) return

    // Remove qualquer popup flutuante existente
    document.querySelectorAll(".opacity-popup-floating").forEach(p => p.remove())

    // Remove do container original e adiciona no body
    popup.classList.remove("hidden")
    popup.classList.add("opacity-popup-floating")
    popup.style.position = "absolute"
    popup.style.zIndex = 9999
    popup.style.minWidth = "160px"
    popup.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)"
    popup.style.backgroundColor = "white"
    popup.style.borderRadius = "8px"

    // Adiciona ao body
    document.body.appendChild(popup)

    // Calcula posição
    const rect = button.getBoundingClientRect()
    const scrollY = window.scrollY || document.documentElement.scrollTop
    const scrollX = window.scrollX || document.documentElement.scrollLeft
    const popupRect = popup.getBoundingClientRect()

    const top = rect.bottom + popupRect.height > window.innerHeight
      ? rect.top + scrollY - popupRect.height - 4
      : rect.bottom + scrollY + 4

    popup.style.left = `${rect.left + scrollX - 140}px`
    popup.style.top = `${top}px`

    // Fecha ao clicar fora
    const closePopup = (e) => {
      if (!popup.contains(e.target) && e.target !== button) {
        popup.classList.add("hidden")
        popup.classList.remove("opacity-popup-floating")
        document.body.appendChild(popup) // retorna para o container original se quiser
        document.removeEventListener("click", closePopup)
      }
    }

    document.addEventListener("click", closePopup)
  }

  changeLayerOpacity(event) {
    const layerId = String(event.target.dataset.layerId)
    const opacity = parseFloat(event.target.value)
    if (isNaN(opacity)) return
    this.layerOpacities[layerId] = opacity

    this.applyLayerOpacitiesAndColors()
  }

  toggleExpand() {
    const container = this.containerTarget
    const mapWrapper = this.mapWrapperTarget
    const statsSection = this.statsSectionTarget
    const chevronButton = this.chevronButtonTarget

    const expanded = container.classList.toggle("expanded")

    if (expanded) {
      statsSection.classList.add("opacity-0");
      statsSection.classList.add("pointer-events-none");
      statsSection.style.maxHeight = "0";
      mapWrapper.style.maxHeight = "calc(100vh - 84px)";
      chevronButton.querySelector("svg").style.transform = "rotate(180deg)";
    } else {
      statsSection.classList.remove("pointer-events-none");
      statsSection.classList.remove("opacity-0");
      statsSection.style.maxHeight = "40vh";
      chevronButton.querySelector("svg").style.transform = "rotate(0deg)";
    }

    if (this.map) {
      setTimeout(() => this.map.resize(), 400)
    }
  }

  getMonthsBefore({ year, month }, n = 3) {
    year = parseInt(year, 10)
    month = parseInt(month, 10)

    let newYear = year
    let newMonth = month - n

    while (newMonth <= 0) {
      newMonth += 12
      newYear -= 1
    }

    return { year: newYear, month: newMonth }
  }

  getMonthsAfter({ year, month }, n = 3) {
    year = parseInt(year, 10)
    month = parseInt(month, 10)

    let newYear = year
    let newMonth = month + n

    while (newMonth > 12) {
      newMonth -= 12
      newYear += 1
    }

    return { year: newYear, month: newMonth }
  }

  resetFilters() {
    const selects = [
      this.citySelectTarget,
      this.roadSelectTarget,
      this.animalClassSelectTarget,
      this.animalSpeciesSelectTarget,
      this.originSelectTarget
    ].filter(Boolean)

    selects.forEach(select => {
      const tom = select.tomselect
      if (tom) {
        tom.clear(true)
      } else {
        select.selectedIndex = 0
      }
    })

    if (this.hasRoadCitySelectTarget) {
      const radios = this.roadCitySelectTarget.querySelectorAll('input[type="radio"]')
      radios.forEach((r, index) => r.checked = index === 0)

      radios[0].dispatchEvent(new Event("change", { bubbles: true }))
    }

    if (this.rangeSliderTarget?.noUiSlider) {
      const startDate = {
        year: this.rangeSliderTarget.dataset["startYear"],
        month: this.rangeSliderTarget.dataset["startMonth"]
      }

      const endDate = {
        year: this.rangeSliderTarget.dataset["endYear"],
        month: this.rangeSliderTarget.dataset["endMonth"]
      }

      this.rangeSliderTarget.noUiSlider.set([
        this.toSliderValue(startDate),
        this.toSliderValue(endDate)
      ])
    }

    this.activeLayerIds = []
    this.layerOpacities = {}

    document.querySelectorAll('.toggle-switch input[type="checkbox"]').forEach(cb => {
      cb.checked = false
    })

    this.clearLayerFeatures()
    this.applyLayerOpacitiesAndColors()

    this.loadOccurrences()
  }

  bindPublicLayerInteractions(sourceId) {
    const layerId = `${sourceId}-points`

    if (!this.map.getLayer(layerId)) return

    if (!this.boundPublicLayers) this.boundPublicLayers = new Set()
    if (this.boundPublicLayers.has(layerId)) return
    this.boundPublicLayers.add(layerId)

    this.map.on("click", layerId, (e) => {
      const feature = e.features[0]
      if (!feature) return

      const featurableId = feature.properties.featurable_id

      const isClickable = this.activeLayerIds.find(l => l.id == featurableId)
      const checkbox = document.querySelector(`input[value="${featurableId}"]`)

      if (!checkbox || checkbox.dataset.publicShow !== "true") return

      const layerName = feature.properties.layer_name || "Camada"
      let publicData = feature.properties.public_data

      if (typeof publicData === "string") {
        try {
          publicData = JSON.parse(publicData)
        } catch {
          publicData = []
        }
      }

      if (!Array.isArray(publicData)) {
        publicData = []
      }

      const coords = feature.geometry.coordinates.slice()

      const html = `
        <div class="bg-white rounded-xl p-3">
          
          <!-- TÍTULO -->
          <div class="mb-2 border-b pb-1">
            <p class="font-semibold text-gray-800">${layerName}</p>
          </div>

          <!-- DADOS -->
          <div class="grid grid-cols-1 gap-x-4 gap-y-2 text-sm text-gray-800">
            ${publicData.map(item => `
              <div>
                <p class="text-gray-500 font-medium mb-1">${item.label}</p>
                <p class="font-semibold">${item.value ?? "—"}</p>
              </div>
            `).join("")}
          </div>
        </div>
      `

      if (this.currentPopup) this.currentPopup.remove()

      this.currentPopup = new maplibregl.Popup({
        className: 'custom-popup',
        closeButton: true,
        closeOnClick: true
      })
        .setLngLat(coords)
        .setHTML(html)
        .addTo(this.map)
    })

    // cursor pointer
    this.map.on("mouseenter", layerId, () => {
      this.map.getCanvas().style.cursor = "pointer"
    })

    this.map.on("mouseleave", layerId, () => {
      this.map.getCanvas().style.cursor = ""
    })
  }
}
